/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../core/components';
import { Component } from '@angular/core';
import { ErrorResponseModel, ErrorsResponseModel } from '../../../../../core/models';
import {
    LanguageService,
    LoadingService,
    LogService,
    ModalService,
    NotifyService,
    RbacService,
    ValidationService
} from '../../../../../core/services';
import { NetworkingService, StaticIpsService } from '../../../shared/services/components';
import { SIPApiService } from '../../../shared/http';
import { SipModel } from '../../../shared/models';
import { TableMultiSelectModel } from '../../../../../core/models/tableMultiSelect.model';
import { catchError, mergeMap, takeUntil } from 'rxjs/operators';
import { from, Observable, of } from 'rxjs';

@Component({
    selector: 'app-sips',
    templateUrl: './sips.component.html',
    styleUrls: ['./sips.component.scss']
})

export class SipsComponent extends BaseComponent {
    /**
     * Table row data.
     */
    public tableRows: any[] = [];

    /**
     * Temporary list of table objects used for searching (Table ONLY).
     */
    public tempRows: any[] = [];

    /**
     * Store selected row object for table state checks.
     */
    public selectedRowObject: any = [];

    /**
     * To store the actions and text for the tables select all functions.
     */
    public multiSelectActions: TableMultiSelectModel[] = [];

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    /**
     * Store selected row key.
     * @private
     */
    private _setSelectedKey: string;

    constructor(
        public modalService: ModalService,
        private languageService: LanguageService,
        private loadingService: LoadingService,
        private logService: LogService,
        private networkingService: NetworkingService,
        private notifyService: NotifyService,
        private rbacService: RbacService,
        private route: ActivatedRoute,
        private router: Router,
        private sipApiService: SIPApiService,
        private staticIpService: StaticIpsService
    ) {
        super();

        this.setUpPageData();
        this.registerButtons();
        this.selectAllActionDotButtons();
    }

    /**
     * Get the selected key.
     */
    get selectedKey(): any {
        return this._setSelectedKey;
    }

    /**
     * Returns formatted string for displaying in the table.
     * @param label
     * @param network
     * @param mask
     * @private
     */
    private static formatNetworkData(label: string, network: any, mask: any): string {
        const ipCheck = label === 'IPv4' ? 'ipV4Check' : 'ipV6Check';
        if (ValidationService[ipCheck](network)) {
            return network && mask ? `${label} ${network}/${mask}` : '';
        }
    }

    /**
     * Confirm delete ip from group
     * @param row
     */
    public confirmDeleteIndividualIP(row: SipModel): void {
        const confirmationObject = {
            title: 'delete_ip',
            message: 'Are you sure you would like to delete IP ' + row.ip + '?',
            callBack: this.deleteIP.bind(this, row.id),
            options: { translateText: false }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Add or edit IP.
     * @param row
     */
    public addEditSIP(row?: any): void {
        const url = row ? 'edit/' + row.id : 'add/';
        this.router.navigate([url], { relativeTo: this.route }).then();
    }

    /**
     * Get message helper. Tooltip.
     * @param row
     */
    public getUsageMessage(row: SipModel): string {
        let message: string;
        if (row.usage?.length > 1) {
            // eslint-disable-next-line max-len
            message = `${this.languageService.translate('static_ip_used_in')} ${row.usage.length} ${this.languageService.translate('places')}`;
        } else if (row.usage?.length === 1) {
            // eslint-disable-next-line max-len
            message = `${this.languageService.translate('static_ip_used_in')} ${row.usage.length} ${this.languageService.translate('place')}`;
        } else {
            message = this.languageService.translate('static_ip_no_usage');
        }

        return message;
    }

    /**
     * Is this sip bound to the user interface? This is work-around until the
     * issues with pulseHA are resolved. See clickUp:
     * https://app.clickup.com/t/4691947/LOAD-4213?comment=90120017622671
     * @param row
     */
    public isBoundToUserInterface(row: SipModel): boolean {
        return !!row.usage.find((item: { service: string }): boolean => item.service === 'userInterface');
    }

    /**
     * Sets the static IP data from the API response.
     * @private
     */
    private setUpPageData(): void {
        this.staticIpService.getStaticIpData()
            .pipe(takeUntil(this.destroyed$))
            .subscribe(data => {
                this.populateRows(data);
            });
    }

    /**
     * Populate table rows for displaying in the table.
     * @private
     */
    private populateRows(data: any): void {
        this.tableRows = [];
        for (const ip of data['sips']) {
            const group: any = data['groups'].find((item: { id: any; }): boolean => item.id === ip.groupId);
            const assignedInterface: any = this.networkingService.getLocalAssignedInterface(group, data['interfaces']);
            this.tableRows.push({
                sipGroup: group,
                groupName: `${group.name} (${assignedInterface?.settings?.interfaceName})`,
                id: ip.id,
                ip: ip.ip,
                ipv4: SipsComponent.formatNetworkData('IPv4', group.networkIp, group.networkMask),
                ipv6: SipsComponent.formatNetworkData('IPv6', group.networkIp, group.networkMask),
                usage: ip.usage
            });
        }

        this.tempRows = [...this.tableRows];
        this.selectedRowObject = this.tableRows.filter(row => row.id === this.selectedKey);
    }

    /**
     * Set the select all action dots object to store the button text and action.
     * @private
     */
    private selectAllActionDotButtons(): void {
        this.multiSelectActions = [
            {
                buttonText: 'static_ip_delete_selected',
                buttonAction: this.confirmDeleteMultipleIPs.bind(this),
                buttonDisabled: !this.permissionsLookup['static-ip-delete']
            }
        ];
    }

    /**
     * Trigger onselect function when table row(s) selected.
     * @param event
     */
    public onSelect(event: any): void {
        this.multiSelectActions[0].buttonAction = this.confirmDeleteMultipleIPs.bind(this, event);
    }

    /**
     * Confirm deletion of multiple IPs.
     * @param selectedRows
     * @private
     */
    private confirmDeleteMultipleIPs(selectedRows: any[]): void {
        const message = selectedRows.length > 1
            ? `Are you sure you want to delete ${selectedRows.length} static IPs?`
            : `Are you sure you want to delete static IP '${selectedRows[0].ip}' ?`;
        const confirmationObject = {
            title: 'static_ip_delete_multiple_ips',
            message: message,
            callBack: this.deleteMultipleIPs.bind(this, selectedRows),
            options: { translateText: false }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Delete multiple IPs.
     * @param selectedRows
     * @private
     */
    private deleteMultipleIPs(selectedRows: SipModel[]): Observable<any> {
        return new Observable((observer) => {
            const dataToSubmit = from(selectedRows || []);
            const call = dataToSubmit.pipe(
                mergeMap(row => this.sipApiService.deleteSIP(row.id).pipe(catchError(error => of(error))))
            );
            call.subscribe({
                next: (response: any): void => {
                    if (response.status === 'fail') {
                        this.updateNotification(selectedRows, response.error.errors);
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('SipsComponent:deleteMultipleIPs() error', error.message);
                    observer.error(error);
                },
                complete: (): void => {
                    this.setUpPageData();
                    observer.next();
                    observer.complete();
                }
            });
        });
    }

    /**
     * Helper function to update the current notification with more details. e.g. ip address.
     * @param selectedRows
     * @param errors
     * @private
     */
    private updateNotification(selectedRows: any[], errors: ErrorsResponseModel): void {
        const message = errors[0]?.message;
        const value = errors[0]?.value;
        if (message && value) {
            const notification = this.notifyService.getNotificationsById(`sips-${value}-delete`);
            const sip = selectedRows.find(item => item.id === value);
            notification.message = `${sip.ip} ${message}`;
        }
    }

    /**
     * Delete static IP.
     * @param ipId
     * @private
     */
    private deleteIP(ipId: string): Observable<any> {
        return new Observable((observer) => {
            this.sipApiService.deleteSIP(ipId)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.setUpPageData();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('SipsComponent:deleteIP() error', error.message);
                        observer.error(error);
                        observer.complete();
                    }
                })
                .add(() => {
                    this.loadingService.loadingActionDotsEmitter.next(false);
                });
        });
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'static-ip-add': [{ path: '/sips', methods: ['POST'] }],
            'static-ip-edit': [{ path: '/sips/:id', methods: ['PUT'] }],
            'static-ip-delete': [{ path: '/sips/:id', methods: ['DELETE'] }],
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }
}
